import { useEffect, useRef } from 'react';
import api from '@/lib/api';

/**
 * Hook que activa el tracking automático de ubicación del inspector en tiempo real.
 * - Al iniciar sesión, solicita permiso y comienza a observar la posición.
 * - Envía la ubicación al backend cada `intervalMs` ms (default 60s).
 * - Se detiene automáticamente al desmontar / logout.
 *
 * Uso: llamar desde el layout del inspector (una sola vez por sesión).
 */
export default function useInspectorLocationTracker({ enabled = true, intervalMs = 60000 } = {}) {
    const lastSentRef = useRef(0);
    const watchIdRef = useRef(null);
    const lastPosRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!enabled) return;
        if (!('geolocation' in navigator)) {
            console.warn('[Tracker] Geolocalización no disponible');
            return;
        }

        const patchLocation = async (lat, lng) => {
            try {
                await api.patch('/inspector/me/location', {
                    latitude: parseFloat(lat.toFixed(6)),
                    longitude: parseFloat(lng.toFixed(6)),
                });
                lastSentRef.current = Date.now();
            } catch (e) {
                // Silencio: reintentaremos en el siguiente ciclo
            }
        };

        const onPos = (pos) => {
            lastPosRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            // Envío inmediato de la primera posición para no esperar el intervalo completo
            if (lastSentRef.current === 0) {
                patchLocation(pos.coords.latitude, pos.coords.longitude);
            }
        };
        const onErr = (err) => {
            console.warn('[Tracker] Error geolocalización:', err.message);
        };

        // watchPosition mantiene abierta la escucha; en Android/iOS se actualiza cada pocos segundos.
        watchIdRef.current = navigator.geolocation.watchPosition(onPos, onErr, {
            enableHighAccuracy: true,
            maximumAge: 30000,
            timeout: 20000,
        });

        // Además solicitamos posición inicial inmediata
        navigator.geolocation.getCurrentPosition(onPos, onErr, { enableHighAccuracy: true, timeout: 15000 });

        // Timer que sube la posición actual al backend cada intervalMs
        timerRef.current = setInterval(() => {
            const p = lastPosRef.current;
            if (p) patchLocation(p.lat, p.lng);
        }, intervalMs);

        return () => {
            if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [enabled, intervalMs]);
}
